import React, { useState, useEffect } from "react";
import ProfileSelection from "../../../components/common/ProfileSelection";
import ToastContainer, { useToast } from "../../../components/common/ToastContainer";
import {
    getAccessToken,
    sendAadharOtp,
    verifyAadharOtp,
    sendMobileOtp,
    verifyMobileOtp,
    loginAadharSendOtp,
    loginAadharVerifyOtp,
    loginAbhaSendOtp,
    loginAbhaVerifyOtp,
    getAbhaProfile,
    verifyUser
} from "../../../api/abhaApi";

const AbhaPopup = ({
    abhaTab,
    handleTabSwitch,
    loginType,
    handleLoginTypeSwitch,
    step,
    setStep,
    aadhar,
    setAadhar,
    mobile,
    setMobile,
    abhaId,
    setAbhaId,
    otp,
    setOtp,
    consent,
    setConsent,
    isAadharValid,
    isOtpValid,
    formatAadhar,
    handleCloseModal,
    onProfileSelected // Callback when profile is selected and saved to localStorage
}) => {
    // Initialize toast system
    const { toasts, removeToast, showSuccess, showError, showInfo } = useToast();

    // Button style for consistency
    const buttonStyle = {
        minWidth: 140,
        padding: "8px 16px",
        borderRadius: 6,
        border: "1px solid #2346a0",
        background: "#fff",
        color: "#2346a0",
        fontWeight: 600,
        marginRight: 8,
        cursor: "pointer"
    };
    const activeButtonStyle = {
        ...buttonStyle,
        background: "#2346a0",
        color: "#fff"
    };
    // Input style for reference
    const inputStyle = {
        width: "100%",
        padding: "8px 12px",
        borderRadius: 4,
        border: "1px solid #ccc",
        fontSize: 16,
        marginBottom: 12
    };
    // Local state for API
    const [loading, setLoading] = useState(false);
    const [txnId, setTxnId] = useState("");
    const [accessToken, setAccessToken] = useState("");

    // Timer state for resend OTP
    const [timer, setTimer] = useState(60);

    // Profile selection state
    const [showProfileSelection, setShowProfileSelection] = useState(false);
    const [verifyResponse, setVerifyResponse] = useState(null);

    // Profile data for auto-fill
    const [profileData, setProfileData] = useState(null);

    const digitsOnly = (value) => String(value || "").replace(/\D/g, "");
    const validateOtpRequest = (value) => {
        if (digitsOnly(value).length !== 6) {
            showError("OTP must contain exactly 6 digits.");
            return false;
        }
        if (!consent) {
            showError("You must accept the consent statement before continuing.");
            return false;
        }
        return true;
    };

    // Timer effect for resend OTP countdown
    useEffect(() => {
        let interval;
        if (step === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    // Function to start timer
    const startTimer = () => {
        setTimer(60);
    };

    // Function to format timer display
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Helper function to call profile API after successful OTP verification
    const callProfileApi = async (xToken) => {
        try {

            try {
                const profileApiResponse = await getAbhaProfile(xToken);


                // Store the profile data for auto-fill
                if (profileApiResponse.success && profileApiResponse.data) {
                    setProfileData(profileApiResponse.data);

                }
                return profileApiResponse;
            } catch (apiError) {
                console.error("Profile API call error:", apiError);
                console.error("API Error details:", {
                    status: apiError.response?.status,
                    statusText: apiError.response?.statusText,
                    data: apiError.response?.data
                });
            }
        } catch (err) {
            console.error("Error extracting tokens for profile API:", err);
        }
    };


    // Helper function to prepare profile data from profile API response
    const prepareRegistrationData = (profileApiData) => {
        if (!profileApiData) return null;

        const getDob = () => {
            if (profileApiData.dob) return profileApiData.dob;
            if (profileApiData.dateOfBirth) return profileApiData.dateOfBirth;

            const year = profileApiData.yearOfBirth;
            const month = profileApiData.monthOfBirth;
            const day = profileApiData.dayOfBirth;

            if (!year) return "";

            const y = String(year).padStart(4, "0");
            const m = month ? String(month).padStart(2, "0") : "01";
            const d = day ? String(day).padStart(2, "0") : "01";

            return `${y}-${m}-${d}`;
        };

        return {
            // ABHA fields
            abhaNumber: profileApiData.ABHANumber?.replace(/-/g, "") || "",
            abhaAddress: profileApiData.preferredAbhaAddress || "",

            // Personal information
            firstName: profileApiData.firstName || "",
            middleName: profileApiData.middleName || "",
            lastName: profileApiData.lastName || "",
            name: profileApiData.name || `${profileApiData.firstName || ""} ${profileApiData.lastName || ""}`.trim(),

            // Demographics
            gender: profileApiData.gender || "M", // M/F

            // Date of birth components
            yearOfBirth: profileApiData.yearOfBirth || "",
            monthOfBirth: profileApiData.monthOfBirth || "",
            dayOfBirth: profileApiData.dayOfBirth || "",
            dob: getDob(),

            // Contact information
            mobile: profileApiData.mobile || "",
            mobileVerified: profileApiData.mobileVerified || false,

            // Address information
            address: profileApiData.address || "",
            pincode: profileApiData.pincode || "",
            stateCode: profileApiData.stateCode || "",
            districtCode: profileApiData.districtCode || "",
            stateName: profileApiData.stateName || "",
            districtName: profileApiData.districtName || "",
            subdistrictName: profileApiData.subdistrictName || "",
            townName: profileApiData.townName || "",

            // Profile images (base64)
            profilePhoto: profileApiData.profilePhoto || "",
            kycPhoto: profileApiData.kycPhoto || "",

            // Verification status
            kycVerified: profileApiData.kycVerified || false,
            verificationStatus: profileApiData.verificationStatus || "",
            verificationType: profileApiData.verificationType || "",

            // Other fields
            authMethods: profileApiData.authMethods || [],
            tags: profileApiData.tags || {},
            source: profileApiData.source || "",
            createdDate: profileApiData.createdDate || "",
            status: profileApiData.status || ""
        };
    };

    // Profile selection handlers
    const handleProfileSelect = async (selectedProfile) => {
        setLoading(true);

        try {
            // Prepare profile data using profile data if available
            let profileToSave;
            if (profileData) {
                // Use detailed data from profile API response
                profileToSave = prepareRegistrationData(profileData);
            } else {
                // Fallback to selected profile data
                profileToSave = {
                    abhaNumber: selectedProfile.ABHANumber?.replace(/-/g, "") || "",
                    abhaAddress: selectedProfile.preferredAbhaAddress || "",
                    name: selectedProfile.name || "",
                    gender: selectedProfile.gender || "M"
                };

                // Extract dob / year from dob field if available
                if (selectedProfile.dob) {
                    profileToSave.dob = selectedProfile.dob;

                    const dobParts = selectedProfile.dob.split("-");
                    if (dobParts.length === 3) {
                        // Assuming dd-mm-yyyy or yyyy-mm-dd; handle both
                        const [part1, , part3] = dobParts;
                        const year =
                            part3.length === 4
                                ? part3
                                : part1.length === 4
                                    ? part1
                                    : null;

                        if (year) {
                            profileToSave.yearOfBirth = parseInt(year, 10);
                        }
                    }
                }
            }

            // Hide profile selection
            // setShowProfileSelection(false);

            // Save profile data to localStorage
            sessionStorage.setItem('selectedAbhaProfile', JSON.stringify(profileToSave));

            // Call callback with profile data
            if (onProfileSelected) {
                onProfileSelected(profileToSave);
            }

            showSuccess("ABHA profile selected and saved successfully!");

            // Auto-close popup after successful profile selection
            setTimeout(() => {
                handleCloseModal();
            }, 2000); // Close after 2 seconds to allow user to see the success message

        } catch (err) {
            showError(err.message || "Failed to save ABHA profile");
            console.error("Profile save error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleBackFromProfile = () => {
        setShowProfileSelection(false);
        setVerifyResponse(null);
    };

    // Handler for sending OTP for mobile login
    const handleSendMobileOtp = async () => {
        if (digitsOnly(mobile).length !== 10) {
            showError("Mobile number must contain exactly 10 digits.");
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessToken();
            setAccessToken(token);
            const response = await sendMobileOtp(mobile, token);

            const txnIdValue = response.txnId || response.txnid || response.transactionId || response.data?.txnId;
            if (txnIdValue) {
                setTxnId(txnIdValue);
                showSuccess(response.data?.message);
            } else {
                showError("No transaction ID returned from Mobile OTP API");
                return;
            }
            setStep(2);
            startTimer(); // Start countdown timer
        } catch (err) {
            showError(err.message || "Failed to send mobile OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for verifying OTP for mobile login
    const handleVerifyMobileOtp = async () => {
        if (!validateOtpRequest(otp)) return;
        setLoading(true);
        try {
            let token = accessToken;
            if (!token) {
                token = await getAccessToken();
                setAccessToken(token);
            }

            const response = await verifyMobileOtp(txnId, otp, token);

            const dtResponse = response;
            // Call profile API after successful verification
            if (dtResponse.data.authResult === 'success') {
                const verifyuser = await verifyUser(accessToken, dtResponse.data.token, dtResponse.data.accounts[0].ABHANumber, dtResponse.data.txnId);

                if (verifyuser.success) {
                    const xToken = verifyuser.data.token;
                    await callProfileApi(xToken);
                }
            }




            // Check if response has accounts for profile selection
            if (response.success && response.data && response.data.accounts && response.data.accounts.length > 0) {
                setVerifyResponse(response);
                setShowProfileSelection(true);
            } else {
                // No profiles to select, close modal directly
                handleCloseModal();
            }
        } catch (err) {
            showError(err.message || "Failed to verify mobile OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for sending OTP (step 1 -> step 2)
    const handleGetAadharOtp = async () => {
        if (digitsOnly(aadhar).length !== 12 || !consent) {
            showError("Enter a valid 12-digit Aadhaar number and accept the consent statement.");
            return;
        }
        setLoading(true);
        try {
            // Always fetch a fresh access token before sending OTP
            const token = await getAccessToken();
            setAccessToken(token);
            const response = await sendAadharOtp(aadhar.replace(/\D/g, ""), token);
            // Try to set txnId from multiple possible keys
            const txnIdValue = response.txnId || response.txnid || response.transactionId || response.data?.txnId;
            if (txnIdValue) {
                setTxnId(txnIdValue);
                showSuccess(response.data?.message);
            } else {
                showError("No transaction ID returned from OTP API");
                return;
            }
            setStep(2);
            startTimer(); // Start countdown timer
        } catch (err) {
            showError(err.message || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for verifying OTP (step 2 submit)
    const handleVerifyAadharOtp = async () => {
        if (digitsOnly(mobile).length !== 10 || !validateOtpRequest(otp)) return;
        setLoading(true);
        try {
            let token = accessToken;
            if (!token) {
                token = await getAccessToken();
                setAccessToken(token);
            }
            const response = await verifyAadharOtp(txnId, otp, mobile, token);
            const dtResponse = response.data;
            const xToken = dtResponse.tokens?.token;
            if (!xToken) {
                throw new Error("Failed to get token from response");
            }

            // Call profile API to get detailed profile data
            const profileApiResponse = await callProfileApi(xToken);

            // Prepare profile data for localStorage
            let profileToSave;
            if (profileApiResponse && profileApiResponse.success && profileApiResponse.data) {
                // Use comprehensive profile data from API
                profileToSave = prepareRegistrationData(profileApiResponse.data);
            } else {
                // Fallback to basic data from verify response
                console.warn("Profile API failed or no data, using basic profile data");
                profileToSave = {
                    abhaNumber: dtResponse.ABHANumber?.replace(/-/g, "") || "",
                    abhaAddress: dtResponse.preferredAbhaAddress || "",
                    name: dtResponse.name || "",
                    gender: dtResponse.gender || "M",
                    mobile: mobile,
                    yearOfBirth: dtResponse.yearOfBirth ? parseInt(dtResponse.yearOfBirth) : ""
                };

            }

            // Save profile data to localStorage
            sessionStorage.setItem('selectedAbhaProfile', JSON.stringify(profileToSave));


            // Call callback to auto-fill patient fields
            if (onProfileSelected) {
                onProfileSelected(profileToSave);
            }

            showSuccess("ABHA profile created and linked successfully!");

            // Auto-close popup after successful profile creation
            setTimeout(() => {
                handleCloseModal();
            }, 2000); // Close after 2 seconds to allow user to see the success message

        } catch (err) {
            showError(err.message || "Failed to verify OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for sending OTP for aadhar login (login tab)
    const handleSendAadharLoginOtp = async () => {
        if (digitsOnly(aadhar).length !== 12 || !consent) {
            showError("Enter a valid 12-digit Aadhaar number and accept the consent statement.");
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessToken();
            setAccessToken(token);
            const response = await loginAadharSendOtp(aadhar.replace(/\D/g, ""), token);

            const txnIdValue = response.txnId || response.txnid || response.transactionId || response.data?.txnId;
            if (txnIdValue) {
                setTxnId(txnIdValue);
                showSuccess(response.data?.message);
            } else {
                showError("No txnId returned from Aadhar Login OTP API");
            }
            setStep(2);
            startTimer(); // Start countdown timer
        } catch (err) {
            showError(err.message || "Failed to send aadhar login OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for verifying OTP for aadhar login (login tab)
    const handleVerifyAadharLoginOtp = async () => {
        if (!validateOtpRequest(otp)) return;
        setLoading(true);
        try {
            let token = accessToken;
            if (!token) {
                token = await getAccessToken();
                setAccessToken(token);
            }
            const response = await loginAadharVerifyOtp(txnId, otp, token);
            const dtResponse = response;
            const xToken = dtResponse.data.token;
            await callProfileApi(xToken);



            if (response.success && response.data && response.data.accounts && response.data.accounts.length > 0) {
                setVerifyResponse(response);
                setShowProfileSelection(true);
            } else {
                // No profiles to select, close modal directly
                handleCloseModal();
            }
        } catch (err) {
            showError(err.message || "Failed to verify aadhar login OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for sending OTP for abha number login (login tab)
    const handleSendAbhaLoginOtp = async () => {
        if (digitsOnly(abhaId).length !== 14 || !consent) {
            showError("Enter a valid 14-digit ABHA number and accept the consent statement.");
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessToken();
            setAccessToken(token);
            const response = await loginAbhaSendOtp(abhaId, token);

            const txnIdValue = response.txnId || response.txnid || response.transactionId || response.data?.txnId;
            if (txnIdValue) {
                setTxnId(txnIdValue);
                showSuccess(response.data?.message);
            } else {
                showError("No transaction ID returned from ABHA Number Login OTP API");
                return;
            }
            setStep(2);
            startTimer(); // Start countdown timer
        } catch (err) {
            showError(err.message || "Failed to send ABHA number login OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handler for verifying OTP for abha number login (login tab)
    const handleVerifyAbhaLoginOtp = async () => {
        if (!validateOtpRequest(otp)) return;
        setLoading(true);
        try {
            let token = accessToken;
            if (!token) {
                token = await getAccessToken();
                setAccessToken(token);
            }
            const response = await loginAbhaVerifyOtp(txnId, otp, token);
            const dtResponse = response;
            const xToken = dtResponse.data.token;
            await callProfileApi(xToken);

            if (response.success && response.data && response.data.accounts && response.data.accounts.length > 0) {
                setVerifyResponse(response);
                setShowProfileSelection(true);
                showSuccess("ABHA login successful! Please select your profile.");
            } else {
                showInfo("Login successful but no profiles found.");
                // No profiles to select, close modal directly
                handleCloseModal();
            }
        } catch (err) {
            showError(err.message || "Failed to verify ABHA number login OTP");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            {showProfileSelection ? (
                <ProfileSelection
                    profiles={verifyResponse?.data?.accounts || []}
                    onProfileSelect={handleProfileSelect}
                    onBack={handleBackFromProfile}
                />
            ) : (
                <div className="abha-modal-overlay">
                    <div className="abha-modal">
                        <div className="abha-modal-header">
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <h3 style={{ textAlign: "center", color: "#2346a0", marginBottom: 24 }}>Connect/Create ABHA</h3>
                                {/* <img src={require("../../../assets/images/NHIlogo.png")} alt="National Health Authority" style={{ height: 40, marginLeft: 400, marginTop: -20 }} /> */}
                            </div>
                        </div>
                        <div className="abha-modal-body">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div className="abha-login-type-row" style={{ display: "flex", gap: 8 }}>
                                    <button
                                        style={abhaTab === "create" ? activeButtonStyle : buttonStyle}
                                        onClick={() => handleTabSwitch("create")}
                                    >Create ABHA</button>
                                    <button
                                        style={abhaTab === "login" ? activeButtonStyle : buttonStyle}
                                        onClick={() => handleTabSwitch("login")}
                                    >Link ABHA</button>
                                </div>
                            </div>
                            <br></br>
                            {abhaTab === "create" ? (
                                <>
                                    {step === 1 && (
                                        <>
                                            <div className="abha-form-row">
                                                <label>Aadhar Number</label>
                                                <input
                                                    type="text"
                                                    style={inputStyle}
                                                    placeholder="0000 - 0000 - 0000"
                                                    value={aadhar}
                                                    onChange={e => {
                                                        // Only allow digits, max 12
                                                        let val = e.target.value.replace(/\D/g, "").slice(0, 12);
                                                        // Format as 0000 - 0000 - 0000
                                                        let formatted = val.replace(/(\d{4})(\d{4})(\d{0,4})/, (m, p1, p2, p3) => p3 ? `${p1} - ${p2} - ${p3}` : `${p1} - ${p2}`);
                                                        setAadhar(formatted);
                                                    }}
                                                    maxLength={19} required
                                                />
                                            </div>
                                            <div className="abha-consent-row">
                                                <label className="checkBoxCustome">
                                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                                                    <span className="checkmark" />
                                                    <span style={{ marginLeft: 8 }}>
                                                        I agree to Terms and Conditions
                                                        <div style={{ fontSize: 12, color: "#666" }}>
                                                            I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                        </div>
                                                        <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                <button className="customeBtn btnEmpty" onClick={handleCloseModal}>Back</button>
                                                <button className="customeBtn btnLightBlue" disabled={!isAadharValid || !consent || loading} onClick={handleGetAadharOtp}>
                                                    {loading ? "Sending..." : "Get Aadhar OTP"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {step === 2 && (
                                        <>
                                            {/* Only show mobile fields for mobile login */}
                                            {abhaTab === "login" && loginType === "mobile" ? (
                                                <>
                                                    <div className="abha-form-row">
                                                        <label>Mobile Number</label>
                                                        <input type="text" style={inputStyle} value={mobile} disabled required />
                                                    </div>
                                                    <div className="abha-form-row">
                                                        <label>Enter OTP</label>
                                                        <input
                                                            type="text"
                                                            style={inputStyle}
                                                            placeholder="Enter OTP"
                                                            value={otp}
                                                            onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                            maxLength={6} required
                                                        />
                                                        <div style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                                            {timer > 0 ? (
                                                                <span>{formatTimer(timer)} Resend OTP</span>
                                                            ) : (
                                                                <button
                                                                    style={{
                                                                        background: "none",
                                                                        border: "none",
                                                                        color: "#2346a0",
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                        fontSize: 12,
                                                                        padding: 0
                                                                    }}
                                                                    onClick={handleSendMobileOtp}
                                                                    disabled={loading}
                                                                >
                                                                    {loading ? "Sending..." : "Resend OTP"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="abha-consent-row">
                                                        <label className="checkBoxCustome">
                                                            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                            <span className="checkmark" />
                                                            <span style={{ marginLeft: 8 }}>
                                                                I agree to Terms and Conditions
                                                                <div style={{ fontSize: 12, color: "#666" }}>
                                                                    I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                                </div>
                                                                <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                            </span>
                                                        </label>
                                                    </div>
                                                    <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                        <button className="customeBtn btnEmpty" onClick={() => setStep(1)}>Back</button>
                                                        <button
                                                            className="customeBtn btnLightBlue"
                                                            disabled={!(otp && otp.length === 6 && mobile && consent && !loading)}
                                                            onClick={handleVerifyMobileOtp}
                                                        >
                                                            {loading ? "Submitting..." : "Submit"}
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="abha-form-row">
                                                        <label>Aadhar Number</label>
                                                        <input type="text" style={inputStyle} value={aadhar} disabled required />
                                                    </div>
                                                    <div className="abha-form-row">
                                                        <label>Mobile Number</label>
                                                        <input
                                                            type="text"
                                                            style={inputStyle}
                                                            placeholder="Enter Mobile Number"
                                                            value={mobile}
                                                            onChange={e => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                                            maxLength={10} required
                                                        />
                                                    </div>
                                                    <div className="abha-form-row">
                                                        <label>Enter OTP</label>
                                                        <input
                                                            type="text"
                                                            style={inputStyle}
                                                            placeholder="Enter OTP"
                                                            value={otp}
                                                            onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                            maxLength={6} required
                                                        />
                                                        <div style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                                            {timer > 0 ? (
                                                                <span>{formatTimer(timer)} Resend OTP</span>
                                                            ) : (
                                                                <button
                                                                    style={{
                                                                        background: "none",
                                                                        border: "none",
                                                                        color: "#2346a0",
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                        fontSize: 12,
                                                                        padding: 0
                                                                    }}
                                                                    onClick={handleGetAadharOtp}
                                                                    disabled={loading}
                                                                >
                                                                    {loading ? "Sending..." : "Resend OTP"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="abha-consent-row">
                                                        <label className="checkBoxCustome">
                                                            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                            <span className="checkmark" />
                                                            <span style={{ marginLeft: 8 }}>
                                                                I agree to Terms and Conditions
                                                                <div style={{ fontSize: 12, color: "#666" }}>
                                                                    I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                                </div>
                                                                <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                            </span>
                                                        </label>
                                                    </div>
                                                    <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                        <button className="customeBtn btnEmpty" onClick={() => setStep(1)}>Back</button>
                                                        <button className="customeBtn btnLightBlue" disabled={!isOtpValid || !mobile || !consent || loading} onClick={handleVerifyAadharOtp}>
                                                            {loading ? "Submitting..." : "Submit"}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="abha-login-type-row" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                                        <button
                                            style={loginType === "mobile" ? activeButtonStyle : buttonStyle}
                                            onClick={() => handleLoginTypeSwitch("mobile")}
                                        >Link with Mobile</button>
                                        <button
                                            style={loginType === "abha" ? activeButtonStyle : buttonStyle}
                                            onClick={() => handleLoginTypeSwitch("abha")}
                                        >Link with ABHA</button>
                                        <button
                                            style={loginType === "aadhar" ? activeButtonStyle : buttonStyle}
                                            onClick={() => handleLoginTypeSwitch("aadhar")}
                                        >Link with Aadhar</button>
                                    </div>
                                    {step === 1 && (
                                        <>
                                            {loginType === "mobile" && (
                                                <div className="abha-form-row">
                                                    <label>Mobile Number</label>
                                                    <input
                                                        type="text"
                                                        style={inputStyle}
                                                        placeholder="Enter Mobile Number"
                                                        value={mobile}
                                                        onChange={e => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                                        maxLength={10} required
                                                    />
                                                </div>
                                            )}
                                            {loginType === "aadhar" && (
                                                <div className="abha-form-row">
                                                    <label>Aadhar Number</label>
                                                    <input
                                                        type="text"
                                                        style={inputStyle}
                                                        placeholder="0000 - 0000 - 0000"
                                                        value={aadhar}
                                                        onChange={e => {
                                                            // Only allow digits, max 12
                                                            let val = e.target.value.replace(/\D/g, "").slice(0, 12);
                                                            // Format as 0000 - 0000 - 0000
                                                            let formatted = val.replace(/(\d{4})(\d{4})(\d{0,4})/, (m, p1, p2, p3) => p3 ? `${p1} - ${p2} - ${p3}` : `${p1} - ${p2}`);
                                                            setAadhar(formatted);
                                                        }}
                                                        maxLength={19} required
                                                    />
                                                </div>
                                            )}
                                            {loginType === "abha" && (
                                                <div className="abha-form-row">
                                                    <label>ABHA ID</label>
                                                    <input
                                                        type="text"
                                                        style={inputStyle}
                                                        placeholder="xx-xxxx-xxxx-xxxx"
                                                        value={abhaId}
                                                        onChange={e => {
                                                            let val = e.target.value.replace(/\D/g, "").slice(0, 14);
                                                            let formatted = "";
                                                            if (val.length >= 2) {
                                                                formatted = val.slice(0, 2);
                                                                if (val.length > 2) {
                                                                    formatted += "-" + val.slice(2, 6);
                                                                    if (val.length > 6) {
                                                                        formatted += "-" + val.slice(6, 10);
                                                                        if (val.length > 10) {
                                                                            formatted += "-" + val.slice(10, 14);
                                                                        }
                                                                    }
                                                                }
                                                            } else {
                                                                formatted = val;
                                                            }
                                                            setAbhaId(formatted);
                                                        }}
                                                        maxLength={17} required
                                                    />
                                                </div>
                                            )}
                                            <div className="abha-consent-row">
                                                <label className="checkBoxCustome">
                                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                    <span className="checkmark" />
                                                    <span style={{ marginLeft: 8 }}>
                                                        I agree to Terms and Conditions
                                                        <div style={{ fontSize: 12, color: "#666" }}>
                                                            I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                        </div>
                                                        <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                <button className="customeBtn btnEmpty" onClick={handleCloseModal}>Back</button>
                                                {/* If in login tab, use correct OTP send handler for each login type */}
                                                {abhaTab === "login" && loginType === "mobile" ? (
                                                    <button
                                                        className="customeBtn btnLightBlue"
                                                        disabled={!mobile || !consent || loading}
                                                        onClick={handleSendMobileOtp}
                                                    >{loading ? "Sending..." : "Get OTP"}</button>
                                                ) : abhaTab === "login" && loginType === "aadhar" ? (
                                                    <button
                                                        className="customeBtn btnLightBlue"
                                                        disabled={!isAadharValid || !consent || loading}
                                                        onClick={handleSendAadharLoginOtp}
                                                    >{loading ? "Sending..." : "Get OTP"}</button>
                                                ) : abhaTab === "login" && loginType === "abha" ? (
                                                    <button
                                                        className="customeBtn btnLightBlue"
                                                        disabled={!abhaId || !consent || loading}
                                                        onClick={handleSendAbhaLoginOtp}
                                                    >{loading ? "Sending..." : "Get OTP"}</button>
                                                ) : (
                                                    <button
                                                        className="customeBtn btnLightBlue"
                                                        disabled={
                                                            (loginType === "mobile" && (!mobile || !consent)) ||
                                                            (loginType === "aadhar" && (!isAadharValid || !consent)) ||
                                                            (loginType === "abha" && (!abhaId || !consent))
                                                        }
                                                        onClick={() => setStep(2)}
                                                    >Get OTP</button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {step === 2 && abhaTab === "login" && loginType === "mobile" && (
                                        <>
                                            <div className="abha-form-row">
                                                <label>Mobile Number</label>
                                                <input type="text" style={inputStyle} value={mobile} disabled />
                                            </div>
                                            <div className="abha-form-row">
                                                <label>Enter OTP</label>
                                                <input
                                                    type="text"
                                                    style={inputStyle}
                                                    placeholder="Enter OTP"
                                                    value={otp}
                                                    onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    maxLength={6} required
                                                />
                                                <div style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                                    {timer > 0 ? (
                                                        <span>{formatTimer(timer)} Resend OTP</span>
                                                    ) : (
                                                        <button
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                color: "#2346a0",
                                                                cursor: "pointer",
                                                                textDecoration: "underline",
                                                                fontSize: 12,
                                                                padding: 0
                                                            }}
                                                            onClick={handleSendMobileOtp}
                                                            disabled={loading}
                                                        >
                                                            {loading ? "Sending..." : "Resend OTP"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="abha-consent-row">
                                                <label className="checkBoxCustome">
                                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                    <span className="checkmark" />
                                                    <span style={{ marginLeft: 8 }}>
                                                        I agree to Terms and Conditions
                                                        <div style={{ fontSize: 12, color: "#666" }}>
                                                            I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                        </div>
                                                                <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                <button className="customeBtn btnEmpty" onClick={() => setStep(1)}>Back</button>
                                                <button
                                                    className="customeBtn btnLightBlue"
                                                    disabled={!(otp && otp.length === 6 && mobile && consent && !loading)}
                                                    onClick={handleVerifyMobileOtp}
                                                >
                                                    {loading ? "Submitting..." : "Submit"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {step === 2 && abhaTab === "login" && loginType === "aadhar" && (
                                        <>
                                            <div className="abha-form-row">
                                                <label>Aadhar Number</label>
                                                <input type="text" style={inputStyle} value={aadhar} disabled required />
                                            </div>
                                            <div className="abha-form-row">
                                                <label>Enter OTP</label>
                                                <input
                                                    type="text"
                                                    style={inputStyle}
                                                    placeholder="Enter OTP"
                                                    value={otp}
                                                    onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    maxLength={6} required
                                                />
                                                <div style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                                    {timer > 0 ? (
                                                        <span>{formatTimer(timer)} Resend OTP</span>
                                                    ) : (
                                                        <button
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                color: "#2346a0",
                                                                cursor: "pointer",
                                                                textDecoration: "underline",
                                                                fontSize: 12,
                                                                padding: 0
                                                            }}
                                                            onClick={handleSendAadharLoginOtp}
                                                            disabled={loading}
                                                        >
                                                            {loading ? "Sending..." : "Resend OTP"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="abha-consent-row">
                                                <label className="checkBoxCustome">
                                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                    <span className="checkmark" />
                                                    <span style={{ marginLeft: 8 }}>
                                                        I agree to Terms and Conditions
                                                        <div style={{ fontSize: 12, color: "#666" }}>
                                                            I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                        </div>
                                                        <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                <button className="customeBtn btnEmpty" onClick={() => setStep(1)}>Back</button>
                                                <button
                                                    className="customeBtn btnLightBlue"
                                                    disabled={!(otp && otp.length === 6 && aadhar && consent && !loading)}
                                                    onClick={handleVerifyAadharLoginOtp}
                                                >
                                                    {loading ? "Submitting..." : "Submit"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {step === 2 && abhaTab === "login" && loginType === "abha" && (
                                        <>
                                            <div className="abha-form-row">
                                                <label>ABHA ID</label>
                                                <input type="text" style={inputStyle} value={abhaId} disabled required />
                                            </div>
                                            <div className="abha-form-row">
                                                <label>Enter OTP</label>
                                                <input
                                                    type="text"
                                                    style={inputStyle}
                                                    placeholder="Enter OTP"
                                                    value={otp}
                                                    onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    maxLength={6} required
                                                />
                                                <div style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                                    {timer > 0 ? (
                                                        <span>{formatTimer(timer)} Resend OTP</span>
                                                    ) : (
                                                        <button
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                color: "#2346a0",
                                                                cursor: "pointer",
                                                                textDecoration: "underline",
                                                                fontSize: 12,
                                                                padding: 0
                                                            }}
                                                            onClick={handleSendAbhaLoginOtp}
                                                            disabled={loading}
                                                        >
                                                            {loading ? "Sending..." : "Resend OTP"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="abha-consent-row">
                                                <label className="checkBoxCustome">
                                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                    <span className="checkmark" />
                                                    <span style={{ marginLeft: 8 }}>
                                                        I agree to Terms and Conditions
                                                        <div style={{ fontSize: 12, color: "#666" }}>
                                                            I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                        </div>
                                                        <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                <button className="customeBtn btnEmpty" onClick={() => setStep(1)}>Back</button>
                                                <button
                                                    className="customeBtn btnLightBlue"
                                                    disabled={!(otp && otp.length === 6 && abhaId && consent && !loading)}
                                                    onClick={handleVerifyAbhaLoginOtp}
                                                >
                                                    {loading ? "Submitting..." : "Submit"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {step === 2 && !(abhaTab === "login" && (loginType === "mobile" || loginType === "aadhar" || loginType === "abha")) && (
                                        <>
                                            {loginType === "aadhar" && (
                                                <div className="abha-form-row">
                                                    <label>Aadhar Number</label>
                                                    <input type="text" style={inputStyle} value={aadhar} disabled required />
                                                </div>
                                            )}
                                            {loginType === "abha" && (
                                                <div className="abha-form-row">
                                                    <label>ABHA ID</label>
                                                    <input type="text" style={inputStyle} value={abhaId} disabled required />
                                                </div>
                                            )}
                                            <div className="abha-form-row">
                                                <label>Mobile Number</label>
                                                <input
                                                    type="text"
                                                    style={inputStyle}
                                                    placeholder="Enter Mobile Number"
                                                    value={mobile}
                                                    onChange={e => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                                    maxLength={10} required
                                                />
                                            </div>
                                            <div className="abha-form-row">
                                                <label>Enter OTP</label>
                                                <input
                                                    type="text"
                                                    style={inputStyle}
                                                    placeholder="Enter OTP"
                                                    value={otp}
                                                    onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    maxLength={6} required
                                                />
                                                <div style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                                    {timer > 0 ? (
                                                        <span>{formatTimer(timer)} Resend OTP</span>
                                                    ) : (
                                                        <button
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                color: "#2346a0",
                                                                cursor: "pointer",
                                                                textDecoration: "underline",
                                                                fontSize: 12,
                                                                padding: 0
                                                            }}
                                                            onClick={() => {
                                                                if (loginType === "aadhar") {
                                                                    handleSendAadharLoginOtp();
                                                                } else if (loginType === "abha") {
                                                                    handleSendAbhaLoginOtp();
                                                                } else {
                                                                    handleGetAadharOtp();
                                                                }
                                                            }}
                                                            disabled={loading}
                                                        >
                                                            {loading ? "Sending..." : "Resend OTP"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="abha-consent-row">
                                                <label className="checkBoxCustome">
                                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required />
                                                    <span className="checkmark" />
                                                    <span style={{ marginLeft: 8 }}>
                                                        I agree to Terms and Conditions
                                                        <div style={{ fontSize: 12, color: "#666" }}>
                                                            I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA)
                                                        </div>
                                                        <a href="/terms-and-conditions" style={{ fontSize: 12 }}>Read More</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="abha-modal-actions" style={{ marginTop: 16 }}>
                                                <button className="customeBtn btnEmpty" onClick={() => setStep(1)}>Back</button>
                                                <button className="customeBtn btnLightBlue" disabled={!isOtpValid || !mobile || !consent || loading} onClick={handleVerifyAadharOtp}>
                                                    {loading ? "Submitting..." : "Submit"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AbhaPopup;
